import { useEffect, useRef, useCallback } from 'react';

const ParticleNetwork = ({ 
  color = '#02d871', 
  lineColor = '#02d871', 
  particleCount = 70, 
  maxDistance = 140, 
  speed = 0.35,
  mouseRadius = 200,
  mouseRepel = true
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: null, y: null });

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = canvas.parentElement.offsetWidth;
      canvas.height = canvas.parentElement.offsetHeight;
    };
    resize();

    const onResize = () => resize();
    window.addEventListener('resize', onResize);

    // Initialize particles
    particlesRef.current = [];
    for (let i = 0; i < particleCount; i++) {
      particlesRef.current.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * speed * 2,
        vy: (Math.random() - 0.5) * speed * 2,
        radius: Math.random() * 2 + 0.8,
      });
    }

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
    };
    const handleMouseLeave = () => {
      mouseRef.current.x = null;
      mouseRef.current.y = null;
    };
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const particles = particlesRef.current;
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const hasMouse = mx !== null && my !== null;

      for (const p of particles) {
        // Mouse repulsion/attraction
        if (hasMouse && mouseRepel) {
          const dx = p.x - mx;
          const dy = p.y - my;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < mouseRadius && dist > 0) {
            const force = (mouseRadius - dist) / mouseRadius;
            const angle = Math.atan2(dy, dx);
            p.vx += Math.cos(angle) * force * 0.8;
            p.vy += Math.sin(angle) * force * 0.8;
          }
        }

        // Damping
        p.vx *= 0.98;
        p.vy *= 0.98;

        // Minimum velocity
        const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        if (spd < speed * 0.5) {
          p.vx += (Math.random() - 0.5) * speed * 0.3;
          p.vy += (Math.random() - 0.5) * speed * 0.3;
        }

        p.x += p.vx;
        p.y += p.vy;

        // Bounce off edges
        if (p.x < 0) { p.x = 0; p.vx *= -1; }
        if (p.x > canvas.width) { p.x = canvas.width; p.vx *= -1; }
        if (p.y < 0) { p.y = 0; p.vy *= -1; }
        if (p.y > canvas.height) { p.y = canvas.height; p.vy *= -1; }
      }

      // Draw connections between particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDistance) {
            const opacity = (1 - dist / maxDistance) * 0.35;
            ctx.beginPath();
            ctx.globalAlpha = opacity;
            ctx.strokeStyle = lineColor;
            ctx.lineWidth = 0.6;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }

        // Draw connections to mouse cursor
        if (hasMouse) {
          const dx = particles[i].x - mx;
          const dy = particles[i].y - my;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < mouseRadius) {
            const opacity = (1 - dist / mouseRadius) * 0.6;
            ctx.beginPath();
            ctx.globalAlpha = opacity;
            ctx.strokeStyle = lineColor;
            ctx.lineWidth = 1.2;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(mx, my);
            ctx.stroke();
          }
        }

        // Draw particle dot
        ctx.beginPath();
        ctx.globalAlpha = 0.6;
        ctx.fillStyle = color;
        ctx.arc(particles[i].x, particles[i].y, particles[i].radius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw mouse glow
      if (hasMouse) {
        const gradient = ctx.createRadialGradient(mx, my, 0, mx, my, mouseRadius * 0.5);
        gradient.addColorStop(0, color.replace(')', ', 0.08)').replace('rgb', 'rgba'));
        gradient.addColorStop(1, 'transparent');
        ctx.globalAlpha = 1;
        ctx.beginPath();
        ctx.fillStyle = gradient;
        ctx.arc(mx, my, mouseRadius * 0.5, 0, Math.PI * 2);
        ctx.fill();
        
        // Mouse dot
        ctx.beginPath();
        ctx.globalAlpha = 0.8;
        ctx.fillStyle = color;
        ctx.arc(mx, my, 3, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', onResize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [color, lineColor, particleCount, maxDistance, speed, mouseRadius, mouseRepel]);

  useEffect(() => {
    const cleanup = init();
    return cleanup;
  }, [init]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="particle-network-canvas"
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'auto', zIndex: 1 }}
    />
  );
};

export default ParticleNetwork;
